import { DotsVerticalIcon } from '@radix-ui/react-icons';
import { Box, Flex, IconButton, Text } from '@radix-ui/themes';
import { SidebarHelper } from 'classes/helpers/sidebar.helper';
import { CustomIcon } from 'components/common/custom-icon';
import { ErrorBoundary } from 'components/common/error-boundary';
import { LottieFolder } from 'components/common/lottie/folder';
import { CommonMenu } from 'components/common/menu';
import { MachineContext } from 'contexts/machine.context';
import { CustomIconPath } from 'enums/custom.enums';
import { IMenuAction } from 'interfaces/i-menus';
import { ISidebarFolder } from 'interfaces/i-sidebar';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext } from 'react';

const USE_LOTTIE = true;

interface IProps {
  folder: ISidebarFolder;
  isOpen: boolean;
  onToggle: () => void;
  getFolderActions: (folder: ISidebarFolder) => IMenuAction[];
}

/**
 * Folder header row displayed inside the FullMenu virtualized list.
 * Does not render an actual folder, only the header row.
 */
export const Folder = (props: IProps) => {
  const { machine } = useContext(MachineContext);

  return (
    <ErrorBoundary identifier="PitchListFolderRow">
      <Flex
        className="Entry SafeHoverWrapper cursor-pointer"
        gap="2"
        p="2"
        pr="0"
        align="center"
        role="button"
        aria-expanded={props.isOpen}
        onClick={(e) => {
          e.stopPropagation();
          props.onToggle();
        }}
      >
        <Box className="FolderIcon">
          {USE_LOTTIE ? (
            <LottieFolder open={props.isOpen} />
          ) : (
            <CustomIcon
              icon={
                props.isOpen
                  ? CustomIconPath.FolderOpen
                  : CustomIconPath.FolderClosed
              }
            />
          )}
        </Box>

        <Box flexGrow="1" title={props.folder.pathDisplay} minWidth="0">
          <Text as="div" truncate>
            {SidebarHelper.getFolderName(props.folder, machine.machineID)}
          </Text>
        </Box>

        <Box className="SafeHoverElement">
          <CommonMenu
            title={'Manage Folder'}
            actions={props.getFolderActions(props.folder)}
            trigger={
              <IconButton
                mr="2"
                className="btn-floating"
                variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                size="1"
                color={RADIX.COLOR.NEUTRAL}
                onClick={(e) => {
                  // prevent the row click handler from toggling open/close
                  // when the user is just opening the menu
                  e.stopPropagation();
                }}
              >
                <DotsVerticalIcon />
              </IconButton>
            }
            skipSort
          />
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
